import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TimeFormat } from '../utils/time';

export type ProgressDisplayMode = 'ring' | 'bar' | 'both' | 'none' | 'dynamic';

/** How the "+N XP" amount reveals on a gain. */
export type XpAnimStyle = 'slide' | 'rise' | 'pop';

/** Where the "+N XP" amount appears along the progress component. */
export type XpAnimPosition = 'left' | 'center' | 'right';

/** Desktop/web reward surfaces — each independently toggleable. */
export type DesktopSurface = 'rail' | 'header' | 'toast';

/** How the domain-level progress is drawn on the desktop rail reward. */
export type DesktopDomainStyle = 'arc' | 'line';

/** Where the desktop XP toast appears — horizontally centered, top or bottom. */
export type ToastPosition = 'top' | 'bottom';

/** Where the active habit-session widget sits in the top bar (relative to the content canvas). */
export type HeaderWidgetPosition = 'left' | 'center' | 'right';

/**
 * How the radial arc on the active routine-timer screen is drawn:
 *  - 'step': one ring for the CURRENT step's countdown only.
 *  - 'routine': the whole routine as domain-coloured wedges with a now position.
 *  - 'layered': outer routine wedges + inner current-step ring.
 */
export type RoutineTimerArc = 'step' | 'routine' | 'layered';

interface ProgressDisplayState {
  mode: ProgressDisplayMode;
  setMode: (mode: ProgressDisplayMode) => void;
  /** Reveal style for the "+N XP" amount chip. */
  xpStyle: XpAnimStyle;
  setXpStyle: (style: XpAnimStyle) => void;
  /** Where the "+N XP" amount appears along the bar. */
  xpPosition: XpAnimPosition;
  setXpPosition: (pos: XpAnimPosition) => void;
  /** Which desktop surfaces show the XP reward (multi-select). */
  desktopSurfaces: DesktopSurface[];
  toggleDesktopSurface: (surface: DesktopSurface) => void;
  /** How the rail reward draws domain progress (concentric arc vs. thin line). */
  desktopDomainStyle: DesktopDomainStyle;
  setDesktopDomainStyle: (style: DesktopDomainStyle) => void;
  /** Where the desktop XP toast slides in (centered top vs. centered bottom). */
  toastPosition: ToastPosition;
  setToastPosition: (pos: ToastPosition) => void;
  /** Where the active habit-session widget sits in the top bar. */
  headerWidgetPosition: HeaderWidgetPosition;
  setHeaderWidgetPosition: (pos: HeaderWidgetPosition) => void;
  /** How the radial arc on the active routine timer is drawn. */
  routineTimerArc: RoutineTimerArc;
  setRoutineTimerArc: (arc: RoutineTimerArc) => void;
  /** How clock times are displayed (24h vs 12h AM/PM) across schedule + templates. */
  timeFormat: TimeFormat;
  setTimeFormat: (format: TimeFormat) => void;
}

/**
 * How the user's level progress is surfaced in the mobile top bar:
 *  - 'ring' (default): a progress ring around the header avatar
 *  - 'bar': a thin line progress bar (hidden while a habit session is active)
 *  - 'both': ring around the avatar AND the line bar
 *  - 'none': no progress UI — just the level badge under the avatar
 *  - 'dynamic': bar hidden; flashes in only when XP is earned (fade in at the
 *    pre-XP state → animate the gain → fade out), then hides again
 * Persisted locally so it survives reloads; purely a presentation preference.
 */
export const useProgressDisplayStore = create<ProgressDisplayState>()(
  persist(
    (set) => ({
      mode: 'ring',
      setMode: (mode) => set({ mode }),
      xpStyle: 'slide',
      setXpStyle: (xpStyle) => set({ xpStyle }),
      xpPosition: 'center',
      setXpPosition: (xpPosition) => set({ xpPosition }),
      desktopSurfaces: ['rail'],
      toggleDesktopSurface: (surface) =>
        set((state) => ({
          desktopSurfaces: state.desktopSurfaces.includes(surface)
            ? state.desktopSurfaces.filter((s) => s !== surface)
            : [...state.desktopSurfaces, surface],
        })),
      desktopDomainStyle: 'arc',
      setDesktopDomainStyle: (desktopDomainStyle) => set({ desktopDomainStyle }),
      toastPosition: 'bottom',
      setToastPosition: (toastPosition) => set({ toastPosition }),
      headerWidgetPosition: 'center',
      setHeaderWidgetPosition: (headerWidgetPosition) => set({ headerWidgetPosition }),
      routineTimerArc: 'step',
      setRoutineTimerArc: (routineTimerArc) => set({ routineTimerArc }),
      timeFormat: '12h',
      setTimeFormat: (timeFormat) => set({ timeFormat }),
    }),
    { name: 'progress-display-storage', version: 1 }
  )
);
